import { EventEmitter, Injectable } from '@angular/core';
import { CHART_API_URL } from 'app/helpers/globals';
import { catchError, finalize, map, share, take, tap, switchMap, timeout } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import moment, { getCurrentTimezone } from 'app/moment.override';
type Moment = typeof moment;
import { TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CharttranslationService } from '../charttranslation.service';
import { CompanyService } from 'app/services/company/company.service';
import { Company } from 'app/services/company/company.model';
import { DecimalPipe } from '@angular/common';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { MatDatepickerInputEvent, MatEndDate, MatStartDate } from '@angular/material/datepicker';
import { NamePipe } from 'app/pipes/common/name-pipe';
import { ProgressBarService } from 'app/services/progress-bar/progress-bar.service';
import { TableInfo } from './models/table.model';
import * as ExcelJS from 'exceljs';
import { NameFormatService } from 'app/services/name-format/name-format.service';
import {
    CardSlot,
    ChartInfo,
    Dailychartinput,
    DataSet,
    DataTypes,
    DriversOnDay,
    DrivingStatus, Fault,
    IconText,
    InfoLineitemDescription,
    Infringment, Offence,
    OneDayActivity,
    OneWorkingTimeRow,
    Place,
    Shift,
    ShiftVehicleInfo,
    WorkingTimeDetails
} from './models/chart.model';
import { LabelledReportContext, ReportContext } from './models/context.model';
import { ReportModel, TabOption } from '../report.model';
import { PrintService } from '../print.service';
import { Observable, of, Subscription, zip } from 'rxjs';
import { downloadBlob, downloadContent, parseContentDispositionFilename, replaceFileExtension } from '../download.util';
import { DateIntervalOptions } from 'app/views/rszoft/report/utils/date.util';
import { OffencesResponse, OffencesService } from 'app/services/offences/offences.service';
import { DiagramPeriodLoaderService } from './diagram-period-loader.service';

const DRIVER_DAILY_DOWNLOAD_PATH = 'chart/driver/daily/download';
const VEHICLE_DAILY_DOWNLOAD_PATH = 'chart/vehicle/daily/download';
const XLSX_EXTENSION = '.xlsx';
/** Fallback page size before the user picks one in the chart paginator. */
const DEFAULT_CHART_PAGE_SIZE = 7;
// A translation lookup must never hold up (or block) the export
const TRANSLATION_LOOKUP_TIMEOUT_IN_MS = 3000;

// Event/fault codes that have their own label in Report.WeeklyTable.FaultCode. Any other low byte is
// either RFU (below 128) or manufacturer specific (128 and above), matching the backend mapping.
const LABELLED_EVENT_FAULT_TYPES = new Set([
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9,
    16, 17, 18, 19, 20, 21, 22, 23, 24,
    32, 33, 34, 35, 36, 37,
    48, 49, 50, 51, 52, 53,
    64
]);

const httpOptions = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json',
    }),
    withCredentials: true
};

export class DailyChartRequestDTO {
    driverIds: number[];
    vehicle: VehicleRequestDTO[];
    clientId: number;
    startDate: string;
    endDate: string;
}

export class VehicleRequestDTO {
    vehicleIdentificationNumber: string;
    vehicleRegistrationNumber: string;
}

export class DailyChartResponse implements ReportModel {
    driverInfo: DriverInfo = new DriverInfo();
    vehicleInfo: VehicleResponseDTO;

    chartInfo: ChartInfo = new ChartInfo();
    tableInfo: TableInfo;
    dailyChartInputsTemp: Dailychartinput[] = [];
    dailyChartInputs: Dailychartinput[] = [];
    reportGenerated: Moment;
    company: string;
    language: string;
    timeZone: string;
}

export class VehicleResponseDTO {
    country: string;
    registrationNumber: string;
    identificationNumber: string;
    odometerBegin: number;
    odometerEnd: number;
    odometerSumma: number;
    queryPeriodInDays: number;
    activeDaysInPeriod: number;
    lastDownloadTime: number;
    lastDownloadTimeMoment: Moment;
    downloadIdentifier: VehicleDownloadIdentifierDTO;
    drivers: VehicleDriverDTO[];
}

export class DriverInfo {
    driverId: number;
    cardHolderSurname: string;
    cardHolderFirstNames: string;
    cardNumber: string;
    cardIssuingMemberState: string;
    cardHolderBirthDate: number;
    cardInfo: CardInfo;
    queryPeriodInDays: number;
    driverActiveDaysInPeriod: number;
    driverCardDownloadTime: number; // stored in millisecond in database...
    driverCardDownloadTimeMoment: Moment;
    vehicles: Vehicle[] = [];
    // periodStartDate: number;
    // periodEndDate: number;

    constructor() {
        this.cardInfo = new CardInfo();
    }
}

export class VehicleDownloadIdentifierDTO {
    manufacturerName: string;
    approvalNumber: string;
    partNumber: string;
    softwareVersion: string;
    serialNumber: number;
    monthYear: string;
    sensorApprovalNumber: string;
    sensorSerialNumber: string;
    sensorMonthYear: string;
}

export class VehicleDriverDTO {
    firstName: string;
    surName: string;
    cardNumber: string;
    birthDate: number;
    birthDateMoment: Moment;
    useFrom: number;
    useFromMoment: Moment;
    useTo: number;
    useToMoment: Moment;
}

export class CardInfo {
    cardApprovalNumber: string;
    serialNumber: string;
    cardExpiryDate: number;
    valid: boolean;
}

export class Vehicle {
    vehicleRegistrationNumber: string;
    vehicleRegistrationNation: string;
    vehicleFirstUse: number;
    vehicleFirstUseMoment: Moment;
    vehicleLastUse: number;
    vehicleLastUseMoment: Moment;
    odometerBegin: number;
    odometerEnd: number;
    odometerSumma: number;
}

export enum ViewOptions {
    TABLE_VIEW,
    CHART_VIEW
}

@Injectable({
    providedIn: 'root'
})
export class DailyChartService {
    reportContexts: LabelledReportContext[];
    chartLanguage: string;

    activeSelector: string;

    interval = 1000 * 60;

    private readonly changeMenuEmitter: EventEmitter<any>;
    viewTab: ViewOptions;
    goToPrint: boolean;
    printingInProgress: boolean;
    companyName: string;

    start = new UntypedFormControl(new Date());
    end = new UntypedFormControl(new Date());
    private dailyChartReportDriverResponseObservable: Observable<any>;
    private dailyChartReportVehicleResponseObservable: Observable<any>;
    private dailyChartSubscription: Subscription;
    private dailyChartDriverRequestDto: DailyChartRequestDTO;
    private dailyChartVehicleRequestDto: DailyChartRequestDTO;
    private headerKeyByEnglish: Map<string, string> = null;

    private static sortVehicleInfoDriverList(vehicleResponseDTO: VehicleResponseDTO): VehicleResponseDTO {
        vehicleResponseDTO.drivers.sort((a, b) => {
            if (a.useFrom < b.useFrom) {
                return -1;
            }

            if (a.useFrom === b.useFrom) {
                return 0;
            }
            return 1;
        })
        return vehicleResponseDTO;
    }

    private static isTodayAfterLastDownloadDate(recordDate: number, lastDownloadedDate: number): boolean {
        const recordDateInMs = recordDate * 1000;
        return recordDateInMs > lastDownloadedDate;
    }

    private static mapOneDayActivityToWorkingTimeDetails(oneDayActivity: OneDayActivity): WorkingTimeDetails {
        const workingTimeDetails = new WorkingTimeDetails();
        if (oneDayActivity) {
            workingTimeDetails.working = oneDayActivity.dailyWorkSumInMinutesOnDay;
            workingTimeDetails.availability = oneDayActivity.dailyAvailabilitySumInMinutesOnDay;
            workingTimeDetails.driving = oneDayActivity.dailyDrivingSumInMinutesOnDay;
            workingTimeDetails.allWorksInDay = workingTimeDetails.getAllWorksSumInDay();
        }
        return workingTimeDetails;
    }

    private static mapPlaceToInfolineDescriptionItem(place: Place, recordDate: number, timestamp: number): InfoLineitemDescription {
        const infoLineitemDescription = new InfoLineitemDescription();
        if (place !== undefined) {
            infoLineitemDescription.text = place.countryShort;
            infoLineitemDescription.crossingMinute = (timestamp - recordDate) / 60;
            return infoLineitemDescription;
        } else {
            return undefined;
        }
    }

    private static isTwoEpocInOneMinute(a: number, b: number) {
        return a - b < 60 && a - b > -60;
    }

    private static isTwoCrossingMinutesInOneMinute(a: number, b: number) {
        return a - b < 1 && a - b > -1;
    }

    private static convertFaultCode(fault: Fault) {
        if (fault.eventFaultType === undefined || fault.eventFaultType === null) {
            return;
        }
        const rawEventFaultType = Number(fault.eventFaultType);
        if (!Number.isFinite(rawEventFaultType)) {
            return;
        }
        // The backend only looks at the low byte of the event/fault type.
        const eventFaultType = rawEventFaultType & 0xff;
        if (LABELLED_EVENT_FAULT_TYPES.has(eventFaultType)) {
            fault.eventFaultType = eventFaultType;
        } else if (eventFaultType < 128) {
            fault.eventFaultType = 127; // RFU
        } else {
            fault.eventFaultType = 256; // Manufacturer specific
        }
    }

    constructor(private http: HttpClient,
        private translate: TranslateService,
        private snack: MatSnackBar,
        private chartTranslationService: CharttranslationService,
        private printService: PrintService,
        private companyService: CompanyService,
        private offencesService: OffencesService,
        private progressBarService: ProgressBarService,
        private decimalPipe: DecimalPipe,
        private formBuilder: UntypedFormBuilder,
        private namePipe: NamePipe,
        private nameFormatService: NameFormatService,
        private diagramPeriodLoader: DiagramPeriodLoaderService) {

        this.initReportContexts();

        this.changeMenuEmitter = new EventEmitter<any>();
        this.activeSelector = 'driver';
        this.progressBarService.mode.next('determinate');
    }

    initReportContexts() {
        this.reportContexts = [];
        this.reportContexts.push(new LabelledReportContext(TabOption.DRIVER, this.initOneReportContext()));
        this.reportContexts.push(new LabelledReportContext(TabOption.VEHICLE, this.initOneReportContext()));
    }

    initOneReportContext(): ReportContext {
        return new ReportContext(this.formBuilder, this.start, this.end);
        // const reportContext = new ReportContext(this.formBuilder, this.start, this.end);
        // reportContext.chartQueryForm.valueChanges.subscribe(value => {
        //     this.reportContexts.forEach(labelledReportContext => {
        //         // if (value.rangeFormGroup.start !== labelledReportContext.reportContext.chartQueryForm.get('rangeFormGroup').get('start').value) {
        //         //     this.start.setValue(Date.parse(value.rangeFormGroup.start));
        //         // }
        //         labelledReportContext.reportContext.chartQueryForm.get('rangeFormGroup').
        //     });
        // if (value.rangeFormGroup.end !== this.end.value) {
        //     this.end.setValue(Date.parse(value.rangeFormGroup.end));
        // }
        // this.reportContexts.forEach(labelledReportContext => {
        //     if (labelledReportContext.reportContext.chartQueryForm.get('rangeFormGroup').get('start') !== value.rangeFormGroup.start) {
        //         labelledReportContext.reportContext.chartQueryForm.get('rangeFormGroup').get('start').setValue(value.rangeFormGroup.start);
        //     }
        //     if (labelledReportContext.reportContext.chartQueryForm.get('rangeFormGroup').get('end') !== value.rangeFormGroup.end) {
        //         labelledReportContext.reportContext.chartQueryForm.get('rangeFormGroup').get('end').setValue(value.rangeFormGroup.end);
        //     }
        // });
        // })
        // return reportContext;
    }

    getReportContext(label: string): LabelledReportContext {
        return this.reportContexts.find(reportContext => {
            return reportContext.label === label;
        })
    }

    getDriverDailyChartReport(dailyChartDto: DailyChartRequestDTO) {
        if (this.dailyChartReportDriverResponseObservable) {
            return null;
        } else {
            this.dailyChartDriverRequestDto = dailyChartDto;
            this.dailyChartReportDriverResponseObservable = this.http.post<DailyChartResponse[]>(CHART_API_URL + 'chart/driver/daily',
                JSON.stringify(dailyChartDto), httpOptions)
                .pipe(
                    share(),
                    map(response => {
                        response.forEach(dailyChartResponse => {
                            dailyChartResponse.timeZone = getCurrentTimezone();
                        });
                        return response;
                    }),
                    take(1),
                    finalize(() => this.dailyChartReportDriverResponseObservable = null));
            return this.dailyChartReportDriverResponseObservable;
        }
    }

    getVehicleDailyChartReport(dailyChartDto: DailyChartRequestDTO) {
        if (this.dailyChartReportVehicleResponseObservable) {
            return null;
        } else {
            this.dailyChartVehicleRequestDto = dailyChartDto;
            this.dailyChartReportVehicleResponseObservable = this.http.post<DailyChartResponse[]>(CHART_API_URL + 'chart/vehicle/daily',
                JSON.stringify(dailyChartDto), httpOptions)
                .pipe(
                    share(),
                    take(1),
                    map(response => {
                        response.forEach(resp => {
                            resp.timeZone = getCurrentTimezone();
                            resp.chartInfo.eventsOnDays.forEach(eventsOnDay => {
                                eventsOnDay.faults = eventsOnDay.faults.map(fault => {
                                    const f = fault as any;
                                    const fa = new Fault();
                                    fa.duration = f?.duration ?? 0,
                                        fa.eventBeginTime = f.faultBeginTime,
                                        fa.eventEndTime = f.faultEndTime,
                                        fa.eventFaultType = f.faultType;
                                    return fa;
                                });
                                (eventsOnDay as any).events.map(fault => {
                                    const f = fault as any;
                                    const fa = new Fault();
                                    fa.duration = f?.duration ?? 0,
                                        fa.eventBeginTime = f.eventBeginTime,
                                        fa.eventEndTime = f.eventEndTime,
                                        fa.eventFaultType = f.eventType;
                                    eventsOnDay.faults.push(fa);
                                });
                                const mergedCardlessDriving = this.mergeCardlessDriving(
                                    eventsOnDay.cardlessDriving,
                                    eventsOnDay.cardlessDrivingFromActivities);
                                eventsOnDay.cardlessDriving = mergedCardlessDriving;
                                eventsOnDay.cardlessDrivingFromActivities = mergedCardlessDriving;
                            })
                        });
                        return response;
                    }),
                    finalize(() => this.dailyChartReportVehicleResponseObservable = null));
            return this.dailyChartReportVehicleResponseObservable;
        }
    }

    generateDriverCsvExport(): Observable<any> {
        return this.generateCsvExport(DRIVER_DAILY_DOWNLOAD_PATH, this.dailyChartDriverRequestDto);
    }

    generateVehicleCsvExport(): Observable<any> {
        return this.generateCsvExport(VEHICLE_DAILY_DOWNLOAD_PATH, this.dailyChartVehicleRequestDto);
    }

    private generateCsvExport(path: string, dailyChartRequestDto: DailyChartRequestDTO): Observable<any> {
        this.progressBarService.needed.next(true);
        this.progressBarService.mode.next('query');
        return this.http
            .post(CHART_API_URL + path, JSON.stringify(dailyChartRequestDto),
                { ...httpOptions, observe: 'response', responseType: 'blob' })
            .pipe(
                take(1),
                tap(downloadContent),
                finalize(() => this.progressBarService.needed.next(false))
            );
    }

    generateDriverXlsxExport(): Observable<any> {
        return this.generateXlsxExport(DRIVER_DAILY_DOWNLOAD_PATH, this.dailyChartDriverRequestDto);
    }

    generateVehicleXlsxExport(): Observable<any> {
        return this.generateXlsxExport(VEHICLE_DAILY_DOWNLOAD_PATH, this.dailyChartVehicleRequestDto);
    }

    /**
     * The endpoint answers with a CSV, which is converted to a real XLSX workbook in the browser.
     * The name of the downloaded file is always forced to .xlsx, whatever the endpoint puts into
     * the Content-Disposition header (it answers with the .csv name of the request).
     */
    private generateXlsxExport(path: string, dailyChartRequestDto: DailyChartRequestDTO): Observable<any> {
        this.progressBarService.needed.next(true);
        this.progressBarService.mode.next('query');
        return this.http
            .post(CHART_API_URL + path, JSON.stringify(dailyChartRequestDto),
                { ...httpOptions, observe: 'response', responseType: 'blob' })
            .pipe(
                take(1),
                switchMap(async (response: HttpResponse<Blob>) => {
                    const csvText = await response.body.text();
                    const xlsxBlob = await this.convertCsvToXlsx(csvText);
                    const serverFilename = parseContentDispositionFilename(
                        response.headers?.get('content-disposition'));
                    downloadBlob(xlsxBlob, replaceFileExtension(serverFilename, XLSX_EXTENSION));
                    return response;
                }),
                catchError(() => {
                    this.showDownloadError();
                    return of(null);
                }),
                finalize(() => this.progressBarService.needed.next(false))
            );
    }

    private showDownloadError() {
        this.snack.open(this.translate.instant('Report.DailyChart.ErrorMessages.unableToDownloadData'),
            'OK', { duration: 4000 });
    }

    private async convertCsvToXlsx(csvText: string): Promise<Blob> {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet1');

        // Drop the byte order mark some exports start with, it would end up in the first header cell
        const content = (csvText || '').replace(/^\uFEFF/, '');

        // Detect separator automatically
        const separator = this.detectCsvSeparator(content);

        // Parse CSV
        const lines = content.split('\n').filter(line => line.trim());
        const rows = lines.map(line => {
            // Handle CSV parsing with quoted fields
            const result = [];
            let current = '';
            let inQuotes = false;

            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                if (char === '"') {
                    inQuotes = !inQuotes;
                } else if (char === separator && !inQuotes) {
                    result.push(current);
                    current = '';
                } else {
                    current += char;
                }
            }
            result.push(current);

            return result;
        });

        // Translate the (English) header row into the chart language. Best effort only: whatever
        // happens here, the workbook still has to be produced and downloaded.
        if (rows.length > 0) {
            try {
                rows[0] = await this.translateHeaderRow(rows[0]);
            } catch (error) {
                // keep the untranslated header row
            }
        }

        // Add rows to worksheet
        rows.forEach((row, index) => {
            worksheet.addRow(row);
            // Style header row
            if (index === 0) {
                worksheet.getRow(1).font = { bold: true };
            }
        });

        // Auto-fit columns (an empty export has no column definitions at all)
        (worksheet.columns || []).forEach(column => {
            let maxLength = 0;
            column.eachCell({ includeEmpty: true }, cell => {
                const cellLength = cell.value ? cell.value.toString().length : 10;
                if (cellLength > maxLength) {
                    maxLength = cellLength;
                }
            });
            column.width = Math.min(maxLength + 2, 50);
        });

        // Generate Excel file
        const buffer = await workbook.xlsx.writeBuffer();
        return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    }

    /**
     * Translates the (English) header row of the exported CSV.
     *
     * Every lookup is awaited with a timeout and falls back to the untranslated header: the export
     * must never be held up (or blocked forever) by a translation that cannot be resolved.
     */
    private async translateHeaderRow(header: string[]): Promise<string[]> {
        const lang = this.chartLanguage || this.translate.currentLang || this.translate.defaultLang || 'en';
        const keyMap = await this.getHeaderKeyMap();
        return Promise.all(header.map(async cell => {
            const normalized = (cell || '').trim().toLowerCase();
            const key = keyMap.get(normalized);
            if (!key) { return cell; }
            // getTextByLanguageAndValue() emits without completing, so all awaited lookups need
            // take(1): rxjs 6 toPromise() only resolves when the observable completes.
            const translated = await this.chartTranslationService.getTextByLanguageAndValue(key, lang)
                .pipe(take(1), timeout(TRANSLATION_LOOKUP_TIMEOUT_IN_MS))
                .toPromise()
                .catch(() => undefined);
            return translated || cell;
        }));
    }

    // Reverse lookup from English header text -> i18n key, limited to the Report namespace.
    private async getHeaderKeyMap(): Promise<Map<string, string>> {
        if (this.headerKeyByEnglish) { return this.headerKeyByEnglish; }
        const map = new Map<string, string>();
        let enTranslations;
        try {
            enTranslations = await this.translate.getTranslation('en')
                .pipe(take(1), timeout(TRANSLATION_LOOKUP_TIMEOUT_IN_MS))
                .toPromise();
        } catch {
            // the English translations are unavailable right now: leave the headers untouched and
            // do not cache the empty map, so the next export can try again
            return map;
        }
        const walk = (node: any, prefix: string) => {
            Object.keys(node || {}).forEach(k => {
                const value = node[k];
                const dottedKey = prefix ? `${prefix}.${k}` : k;
                if (typeof value === 'string') {
                    const normalized = value.trim().toLowerCase();
                    if (normalized && !map.has(normalized)) { map.set(normalized, dottedKey); }
                } else if (value && typeof value === 'object') {
                    walk(value, dottedKey);
                }
            });
        };
        walk({ Report: enTranslations?.Report }, '');
        this.headerKeyByEnglish = map;
        return map;
    }

    private detectCsvSeparator(csvText: string): string {
        const separators = [',', ';', ':', '\t'];
        const lines = csvText.split('\n').filter(line => line.trim()).slice(0, 5); // Check first 5 lines

        if (lines.length === 0) {
            return ','; // Default to comma
        }

        // Count occurrences of each separator outside of quotes
        const separatorCounts: { [key: string]: number[] } = {};

        separators.forEach(sep => {
            separatorCounts[sep] = [];

            lines.forEach(line => {
                let count = 0;
                let inQuotes = false;

                for (let i = 0; i < line.length; i++) {
                    const char = line[i];
                    if (char === '"') {
                        inQuotes = !inQuotes;
                    } else if (char === sep && !inQuotes) {
                        count++;
                    }
                }

                separatorCounts[sep].push(count);
            });
        });

        // Find the separator with the highest and most consistent count
        let bestSeparator = ',';
        let bestScore = -1;

        separators.forEach(sep => {
            const counts = separatorCounts[sep];
            const maxCount = Math.max(...counts);
            const minCount = Math.min(...counts);

            // Check if the separator appears consistently across lines
            if (maxCount > 0 && maxCount === minCount) {
                // Separator appears the same number of times in each line (good consistency)
                const score = maxCount;
                if (score > bestScore) {
                    bestScore = score;
                    bestSeparator = sep;
                }
            } else if (maxCount > 0) {
                // Separator appears but with some variation
                const avgCount = counts.reduce((a, b) => a + b, 0) / counts.length;
                const variance = counts.reduce((sum, count) => sum + Math.pow(count - avgCount, 2), 0) / counts.length;
                // Lower variance is better
                const score = avgCount - variance;
                if (score > bestScore) {
                    bestScore = score;
                    bestSeparator = sep;
                }
            }
        });

        return bestSeparator;
    }

    generateReport(periodStartTime: Moment, periodEndTime: Moment, dailyChartDto: DailyChartRequestDTO, tab: TabOption) {
        this.progressBarService.value.next(2);
        // A new report must not reuse diagrams of a previous one.
        this.diagramPeriodLoader.clear();
        this.getReportContext(tab).reportContext.periodStartTime = periodStartTime.unix();

        //Set to next Sunday if the period end date is not Sunday
        const daysToNextSunday = (7 - periodEndTime.day()) % 7; // Modulo to handle wrapping around

        // Add the calculated days to the current date to get the next Sunday
        periodEndTime = periodEndTime.add(daysToNextSunday, 'days');

        this.getReportContext(tab).reportContext.periodEndTime = periodEndTime.unix();

        switch (tab) {
            case TabOption.DRIVER:
                this.generateDriverReport(dailyChartDto, periodStartTime.unix(), periodEndTime.unix());
                break;
            case TabOption.VEHICLE:
                this.generateVehicleReport(dailyChartDto);
                break;
        }
    }

    generateDriverReport(dailyChartDto: DailyChartRequestDTO, startDate: number, endDate: number) {
        if (!!dailyChartDto.driverIds &&
            !!this.getReportContext(TabOption.DRIVER).reportContext.periodStartTime &&
            !!this.getReportContext(TabOption.DRIVER).reportContext.periodEndTime) {
            this.startDailyChartProgressBar();
            this.resetReportContext(TabOption.DRIVER);

            const obs = this.getDriverDailyChartReport(dailyChartDto);
            const offencesObs = this.offencesService.getOffenceForDrivers(startDate, endDate, dailyChartDto.driverIds, dailyChartDto.clientId);
            if (obs) {
                this.dailyChartSubscription = zip(obs, offencesObs)
                    .pipe(map(([dailyChartResponse, offencesResponse]) => this.mergeOffencesToDriverData(dailyChartResponse, offencesResponse)))
                    .subscribe(
                        (response: DailyChartResponse[]) => this.generateReportFromResponse(response, TabOption.DRIVER),
                        this.handleGenerateReportError
                    );
            }
        }
    }

    private mergeOffencesToDriverData(dailyCharts: DailyChartResponse[], offences: OffencesResponse[]): DailyChartResponse[] {
        dailyCharts.forEach(dailyChartResponse => {
            const offenceList = offences
                .filter(offence => (offence.cardNumber as any as string) === dailyChartResponse.driverInfo.cardNumber)
                .map(offence => offence.offenceByCodes.filter(oneOffence => oneOffence.severity_2006_561 !== 'NONE'));

            if (offenceList?.length === 0) { return; }
            dailyChartResponse.chartInfo.eventsOnDays.forEach(oneDayActivity => {
                oneDayActivity.offences = offenceList[0]
                    .filter(offence => offence.endTime >= oneDayActivity.recordDate && offence.startTime <= oneDayActivity.recordDate + 86399)
                    .map(offence => {
                        return {
                            startTime: offence.startTime,
                            endTime: offence.endTime,
                            type: offence.type,
                            duration: offence.value,
                            norm: offence.norm,
                            severity: offence.severity_2006_561
                        } as Offence;
                    });
                // console.log(oneDayActivity.offences);
            });
        });
        return dailyCharts;
    }

    generateVehicleReport(dailyChartDto: DailyChartRequestDTO) {
        if (!!dailyChartDto.vehicle &&
            !!this.getReportContext(TabOption.VEHICLE).reportContext.periodStartTime &&
            !!this.getReportContext(TabOption.VEHICLE).reportContext.periodEndTime) {
            this.startDailyChartProgressBar();
            this.resetReportContext(TabOption.VEHICLE);

            const obs = this.getVehicleDailyChartReport(dailyChartDto);
            if (obs) {
                obs.subscribe(
                    (response: DailyChartResponse[]) => this.generateReportFromResponse(response, TabOption.VEHICLE),
                    this.handleGenerateReportError
                );
            }
        }
    }

    private generateReportFromResponse(response: DailyChartResponse[], tab: TabOption) {
        this.getReportContext(tab).reportContext.dailyChartResponses = [];
        for (const resp of response) {
            this.chartLanguage = resp.language;
            this.printService.language = resp.language;
            this.chartTranslationService.setCurrentLang(resp.language);
            const oneDailyChartResponse = new DailyChartResponse();
            if (tab === TabOption.DRIVER) {
                oneDailyChartResponse.driverInfo = this.convertDriverInfo(resp.driverInfo, resp.timeZone);
            } else {
                oneDailyChartResponse.vehicleInfo = this.convertVehicleInfo(resp.vehicleInfo, resp.timeZone);
            }
            oneDailyChartResponse.chartInfo = resp.chartInfo;
            oneDailyChartResponse.reportGenerated = moment().tz(resp.timeZone);
            this.printService.reportGenerated = moment().tz(resp.timeZone);
            oneDailyChartResponse.language = resp.language;
            oneDailyChartResponse.timeZone = resp.timeZone;
            this.getReportContext(tab).reportContext.dailyChartInputsTemp = [];
            const requestDto = tab === TabOption.DRIVER ? this.dailyChartDriverRequestDto : this.dailyChartVehicleRequestDto;
            this.initiateDailyChartInputsFromChartInfo(oneDailyChartResponse.chartInfo.eventsOnDays,
                this.getReportContext(tab).reportContext.dailyChartInputsTemp,
                resp.timeZone, resp.language, oneDailyChartResponse.driverInfo, oneDailyChartResponse.vehicleInfo,
                requestDto?.clientId);
            if (tab === TabOption.DRIVER && resp.driverInfo.driverActiveDaysInPeriod > 0 ||
                tab === TabOption.VEHICLE && resp.vehicleInfo.activeDaysInPeriod > 0) {
                oneDailyChartResponse.dailyChartInputs = this.getReportContext(tab).reportContext.dailyChartInputsTemp;
            }
            oneDailyChartResponse.tableInfo = new TableInfo();
            this.initiateTableInfoFromResponse(resp, oneDailyChartResponse.tableInfo,
                tab === TabOption.DRIVER ?
                    oneDailyChartResponse.driverInfo.driverCardDownloadTime :
                    oneDailyChartResponse.vehicleInfo.lastDownloadTime);
            if (tab === TabOption.DRIVER && resp.driverInfo.driverActiveDaysInPeriod > 0 ||
                tab === TabOption.VEHICLE && resp.vehicleInfo.activeDaysInPeriod > 0) {
                Array.prototype.push.apply(this.getReportContext(tab).reportContext.dailyChartInputs,
                    this.getReportContext(tab).reportContext.dailyChartInputsTemp);
            }
            this.getReportContext(tab).reportContext.dailyChartResponses.push(oneDailyChartResponse);
        }
        this.getReportContext(tab).reportContext.chartDataGenerated = true;
        setTimeout(() => {
            this.progressBarService.mode.next('determinate');
            this.progressBarService.value.next(2);
            this.stopDailyChartProgressBar();
            this.getReportContext(tab).reportContext.chartDataReady = true;
            if (this.viewTab === ViewOptions.CHART_VIEW) {
                const pageSize = Number(localStorage.getItem('dailyChartPageSize')) || DEFAULT_CHART_PAGE_SIZE;
                this.updateDailyChartInputsPaged(pageSize, 0, tab);
            }
        });
    }

    private handleGenerateReportError() {
        this.snack.open(this.translate.instant('Report.DailyChart.ErrorMessages.unableToDownloadData'), 'OK', { duration: 4000 });
        this.stopDailyChartProgressBar();
    }

    /**
     * The chart view renders one stacked chart for the whole page, so the page
     * is published at once instead of chart-by-chart.
     */
    updateDailyChartInputsPaged(pageSize: number, pageIndex: number, tab: TabOption) {
        const context = this.getReportContext(tab).reportContext;
        const response = context.dailyChartResponses[context.selectedDriverIndex];
        context.dailyChartInputsPagedTemp = (response?.dailyChartInputs || [])
            .slice(pageIndex * pageSize, pageIndex * pageSize + pageSize);
        context.dailyChartInputsPaged = [...context.dailyChartInputsPagedTemp];
        context.chartDataGenerated = true;
        context.indexOfLastChartToLoad = context.dailyChartInputsPaged.length - 1;
        this.stopDailyChartProgressBar();
        context.onDatasSetChanged().emit();
    }

    fillNextDailyChartInput(event: any, tab: TabOption) {
        const responseIndex = event['responseIndex'];
        // const response = this.getReportContext(tab).reportContext.dailyChartResponses[responseIndex];
        const indexToSet = event['index'] + 1;
        if (indexToSet < this.getReportContext(tab).reportContext.dailyChartResponses[responseIndex].dailyChartInputs.length) {
            this.printService.progressBarValue = Math.round(100 * (this.getReportContext(tab).reportContext.indexOfLastChartToLoad
                / this.getReportContext(tab).reportContext.countOfCharts));
            this.getReportContext(tab).reportContext.dailyChartResponses[responseIndex].dailyChartInputsTemp[indexToSet]
                = this.getReportContext(tab).reportContext.dailyChartResponses[responseIndex].dailyChartInputs[indexToSet];
            this.getReportContext(tab).reportContext.indexOfLastChartToLoad++;
        }

        // const indexToSet = this.indexOfLastChartToLoad + 1;
        //
        // this.progressbarValue = Math.round(100 * (indexToSet / this.dailyChartInputs.length));
        // if (indexToSet < this.dailyChartInputs.length) {
        //     this.dailyChartInputsPaged[indexToSet] = this.dailyChartInputs[indexToSet];
        //     this.indexOfLastChartToLoad = indexToSet;
        //     this.datasetChangeEmitter.emit();
        // }
        // if (indexToSet === this.dailyChartInputs.length) {
        //     this.progressBarNeeded = false;
        //     this.onDataReady();
        // }
    }

    getNextEvent(indexToSet: number, responseIndex: number, tab: TabOption) {
        if (indexToSet + 1 === this.getReportContext(tab).reportContext.dailyChartResponses[responseIndex].dailyChartInputs.length) {
            let nextIndex = responseIndex + 1;
            if (this.getReportContext(tab).reportContext.dailyChartResponses.length > nextIndex) {
                while (nextIndex < this.getReportContext(tab).reportContext.dailyChartResponses.length &&
                    this.getReportContext(tab).reportContext.dailyChartResponses[nextIndex].dailyChartInputs.length === 0) {
                    nextIndex++;
                }
            }
            if (this.getReportContext(tab).reportContext.dailyChartResponses.length === nextIndex) {
                this.dataReadyToPrint(tab);
                this.stopDailyChartProgressBar();
                return undefined;
            } else {
                return { index: -1, responseIndex: nextIndex };
            }
        }
        return { index: indexToSet, responseIndex: responseIndex };
    }

    getNumberOfDailyChartItemsOnPageByPageSizeAndPageIndex(pageSize: number, pageIndex: number, tab: TabOption,
        responseIndex?: number) {
        const remaining = this.getSumOfActivitiesOnDailyChartResponse(tab, responseIndex) - (pageSize * pageIndex);
        return remaining > pageSize ? pageSize : remaining;
    }

    getSumOfActivitiesOnDailyChartResponse(tab: TabOption, responseIndex?: number) {
        if (responseIndex === undefined) {
            return this.getReportContext(tab).reportContext.dailyChartInputs.length;
        } else {
            return this.getReportContext(tab).reportContext.dailyChartResponses[responseIndex].dailyChartInputs.length;
        }
    }

    private convertDriverInfo(driverInfo: DriverInfo, timezone: string): DriverInfo {
        const di = new DriverInfo();
        Object.assign(di, driverInfo);
        di.vehicles.map(vehicle => {
            vehicle.vehicleFirstUseMoment = moment(vehicle.vehicleFirstUse * 1000).tz(timezone);
            vehicle.vehicleLastUseMoment = moment(vehicle.vehicleLastUse * 1000).tz(timezone);
        });
        di.driverCardDownloadTimeMoment = moment(driverInfo.driverCardDownloadTime).tz(timezone);
        return di;
    }

    private convertVehicleInfo(vehicleInfo: VehicleResponseDTO, timezone: string): VehicleResponseDTO {
        const vi = new VehicleResponseDTO();
        Object.assign(vi, vehicleInfo);
        vi.drivers.map(driver => {
            driver.useFromMoment = moment(driver.useFrom * 1000).tz(timezone);
            if (driver.useTo) {
                driver.useToMoment = moment(driver.useTo * 1000).tz(timezone);
            }
            if (driver.birthDate) {
                driver.birthDateMoment = moment(driver.birthDate * 1000).tz(timezone);
            }
        })
        vi.lastDownloadTimeMoment = moment(vehicleInfo.lastDownloadTime).tz(timezone);
        return DailyChartService.sortVehicleInfoDriverList(vi);
    }

    initiateDailyChartInputsFromChartInfo(eventsOnDays: OneDayActivity[], dailyChartInputs: Dailychartinput[],
        timezone: string, language: string, driverInfo: DriverInfo, vehicleInfo: VehicleResponseDTO,
        clientId?: number) {
        for (const oneDayActivitySet of eventsOnDays) {
            if (oneDayActivitySet.recordDate * 1000 > driverInfo?.driverCardDownloadTime) {
                continue;
            }
            const dailyChartInput = new Dailychartinput();
            dailyChartInput.driverId = driverInfo?.driverId;
            dailyChartInput.clientId = clientId;
            dailyChartInput.dateOfChart = oneDayActivitySet.recordDate * 1000;
            dailyChartInput.activities = oneDayActivitySet.activities || [];
            dailyChartInput.workingDetailsForOneDay = DailyChartService.mapOneDayActivityToWorkingTimeDetails(oneDayActivitySet);
            dailyChartInput.workingTimeRows = this.mapShiftsToOneWorkingTimeRows(oneDayActivitySet.shifts, oneDayActivitySet.recordDate, timezone, language);
            dailyChartInput.borderCrossingItems = this.mapShiftsAndPlacesToInfolineItems(oneDayActivitySet.shifts, oneDayActivitySet.places, oneDayActivitySet.recordDate);
            dailyChartInput.vehicleItems = this.mapShiftVehicleInfoToInfoLineItems(oneDayActivitySet.shifts, oneDayActivitySet.recordDate);
            dailyChartInput.ferryItems = this.mapFerryItemsToInfoLineItems(oneDayActivitySet.ferryEntryTimes, oneDayActivitySet.recordDate);
            dailyChartInput.infringments = this.mapInfringmentItemsIntoDailyChartInput(oneDayActivitySet.infringements, timezone, language);
            dailyChartInput.offences = this.mapOffenceItemsIntoDailyChartInput(oneDayActivitySet.offences, timezone, language);
            dailyChartInput.driversOnDay = this.mapDriversOnDayToInfoLineItems(oneDayActivitySet.driversOnDay, oneDayActivitySet.recordDate, timezone);
            dailyChartInput.faults = this.mapFaults(oneDayActivitySet.faults);
            dailyChartInput.drivingsWithoutCard = oneDayActivitySet.cardlessDriving;
            dailyChartInput.noCardInsertedOnDay = this.mapDrivingsWithoutCardToInfolineItems(oneDayActivitySet.cardlessDriving, oneDayActivitySet.recordDate);
            dailyChartInput.timezone = timezone;
            dailyChartInput.driverFirstName = driverInfo.cardHolderFirstNames;
            dailyChartInput.driverLastName = driverInfo.cardHolderSurname;
            dailyChartInput.odometerStart = oneDayActivitySet.odometerStart;
            dailyChartInput.odometerEnd = oneDayActivitySet.odometerEnd;
            dailyChartInput.odometerSum = oneDayActivitySet.odometerSum;
            if (vehicleInfo) {
                dailyChartInput.vehicleRegistrationNumber = vehicleInfo.registrationNumber;
                dailyChartInput.vehicleIdentificationNumber = vehicleInfo.identificationNumber;
                if (oneDayActivitySet.recordDate * 1000 > vehicleInfo.lastDownloadTime) {
                    dailyChartInput.noDataForToday = true;
                }
            }
            const tempDataSets: DataSet[] = [];
            tempDataSets[DataTypes.DRIVING] = new DataSet(DataTypes.DRIVING);
            tempDataSets[DataTypes.WORK] = new DataSet(DataTypes.WORK);
            tempDataSets[DataTypes.AVAILABILITY] = new DataSet(DataTypes.AVAILABILITY);
            tempDataSets[DataTypes.BREAK_REST] = new DataSet(DataTypes.BREAK_REST);
            tempDataSets[DataTypes.CREW_DRIVING] = new DataSet(DataTypes.CREW_DRIVING);

            let lastDownloadedTime = undefined;
            if (vehicleInfo) {
                lastDownloadedTime = vehicleInfo.lastDownloadTime;
            } else if (driverInfo) {
                lastDownloadedTime = driverInfo.driverCardDownloadTime;
            }
            const lastDownloadedDateToday = this.isLastDownloadedDateToday(oneDayActivitySet.recordDate, lastDownloadedTime);

            for (const oneActivity of oneDayActivitySet.activities) {
                if (oneActivity.activity !== DataTypes.FAKE_ACTIVITY && !DailyChartService.isTodayAfterLastDownloadDate(oneDayActivitySet.recordDate, lastDownloadedTime)) {
                    const startInMinute = (oneActivity.start - oneDayActivitySet.recordDate) / 60;
                    let endInMinute = (oneActivity.end - oneDayActivitySet.recordDate) / 60;
                    if (lastDownloadedDateToday) {
                        const minuteOflAstDownload = (lastDownloadedTime - (oneDayActivitySet.recordDate * 1000)) / 1000 / 60;
                        if (endInMinute > minuteOflAstDownload) {
                            endInMinute = minuteOflAstDownload;
                        }
                    }
                    // we have to set empty records between valid data to have correct chart diagram
                    for (let j = tempDataSets[oneActivity.activity].data.length; j < startInMinute; j++) {
                        tempDataSets[oneActivity.activity].data[j] = [dailyChartInput.dateOfChart + (this.interval * j), null];
                    }
                    for (let i = startInMinute; i <= endInMinute; i++) {
                        tempDataSets[oneActivity.activity].data[i] = [dailyChartInput.dateOfChart + (this.interval * i), tempDataSets[oneActivity.activity].getValue()]
                    }
                    if (oneActivity.drivingStatus === DrivingStatus.CREW) {
                        for (let j = tempDataSets[DataTypes.CREW_DRIVING].data.length; j < startInMinute; j++) {
                            tempDataSets[DataTypes.CREW_DRIVING].data[j] = [dailyChartInput.dateOfChart + (this.interval * j), null];
                        }
                        for (let i = startInMinute; i <= endInMinute; i++) {
                            tempDataSets[DataTypes.CREW_DRIVING].data[i] = [dailyChartInput.dateOfChart + (this.interval * i), 0, -1];
                        }
                    }
                }
            }
            dailyChartInput.dataset.push(tempDataSets[DataTypes.DRIVING]);
            dailyChartInput.dataset.push(tempDataSets[DataTypes.WORK]);
            dailyChartInput.dataset.push(tempDataSets[DataTypes.AVAILABILITY]);
            dailyChartInput.dataset.push(tempDataSets[DataTypes.BREAK_REST]);
            dailyChartInput.dataset.push(tempDataSets[DataTypes.CREW_DRIVING]);

            if (lastDownloadedDateToday) {
                dailyChartInput.lastDownloadedTime = lastDownloadedTime;
            }
            dailyChartInputs.push(dailyChartInput);
        }
    }

    initiateTableInfoFromResponse(response, tableInfo: TableInfo, lastDownloadDate: number) {
        // tableInfo.days does not carry cardless driving yet, it is only present on chartInfo.eventsOnDays
        const cardlessDrivingFromActivitiesByDay =
            this.mapCardlessDrivingFromActivitiesByDay(response.chartInfo?.eventsOnDays);
        const weeks = [];
        response.tableInfo.years.forEach(year => {
            year.weeks.forEach(week => {
                week.numberOfYear = year.numberOfYear;
                week.days.forEach(day => {
                    day.noDataForToday = lastDownloadDate <= day.recordDate * 1000;
                    day.recordDateMoment = moment(day.recordDate * 1000).tz(response.timeZone);
                    day.faults = this.mapTableDayFaults(day);
                    day.cardlessDrivingFromActivities =
                        cardlessDrivingFromActivitiesByDay.get(day.recordDate) || 0;
                });

                week.weeklySummary.cardlessDrivingFromActivities = week.days
                    .filter(day => !day.noDataForToday)
                    .reduce((sum, day) => sum + (day.cardlessDrivingFromActivities || 0), 0);

                // Calculate workDays for this week's summary
                week.weeklySummary.workDays = week.days.filter(day =>
                    !day.noDataForToday &&
                    (day.drive > 0 || day.work > 0 || day.availability > 0)
                ).length;
            });
            weeks.push(...year.weeks);
        });
        tableInfo.queryPeriodSum = response.tableInfo.queryPeriodSum;

        // Calculate workDays: count days with actual activity and downloaded data
        tableInfo.queryPeriodSum.workDays = weeks.reduce((total, week) => {
            return total + week.days.filter(day =>
                !day.noDataForToday &&
                (day.drive > 0 || day.work > 0 || day.availability > 0)
            ).length;
        }, 0);
        tableInfo.queryPeriodSum.cardlessDrivingFromActivities = weeks.reduce((total, week) =>
            total + (week.weeklySummary.cardlessDrivingFromActivities || 0), 0);

        tableInfo.weeks = weeks;
    }

    private mapTableDayFaults(day: any): Fault[] {
        // tableInfo.days keeps events and faults in two separate arrays, and the field names differ
        // between the two. Normalise both into the Fault shape used by the weekly table.
        const faults = [
            ...((day.faults || []).map(fault => this.mapFault(fault))),
            ...((day.events || []).map(event => this.mapFault(event)))
        ];
        faults.forEach(fault => DailyChartService.convertFaultCode(fault));
        return faults.sort(Fault.compare);
    }

    private mapFault(rawFault: any): Fault {
        const fault = new Fault();
        fault.duration = rawFault.duration ?? 0;
        fault.eventFaultType = rawFault.eventFaultType ?? rawFault.faultType ?? rawFault.eventType;
        fault.eventBeginTime = rawFault.eventBeginTime ?? rawFault.faultBeginTime;
        fault.eventEndTime = rawFault.eventEndTime ?? rawFault.faultEndTime;
        return fault;
    }

    /**
     * Merges the event-based cardless driving rows with the per-day aggregate calculated from the
     * activities. The activities aggregate already contains the event-based periods, so the totals
     * must not be added together; the merged total is the larger of the two.
     */
    private mergeCardlessDriving(cardlessDriving: Fault[], cardlessDrivingFromActivities: Fault[]): Fault[] {
        const events = cardlessDriving || [];
        const fromActivities = cardlessDrivingFromActivities || [];
        if (events.length === 0 && fromActivities.length === 0) {
            return [];
        }

        const mergedDuration = Math.max(
            this.sumCardlessDrivingDurations(events),
            this.sumCardlessDrivingDurations(fromActivities));
        if (mergedDuration <= 0) {
            return [];
        }

        // The event rows are real intervals; the activities array is a per-day aggregate whose
        // begin/end span the whole day. Prefer the event range when it is available so the merged
        // entry is drawn as the actual cardless period instead of a whole-day band.
        const entriesForRange = events.length > 0 ? events : fromActivities;
        const begins = entriesForRange
            .map(entry => Number(entry.eventBeginTime))
            .filter(begin => Number.isFinite(begin));
        const ends = entriesForRange
            .map(entry => Number(entry.eventEndTime))
            .filter(end => Number.isFinite(end));

        const merged = new Fault();
        merged.duration = mergedDuration;
        merged.eventBeginTime = begins.length ? Math.min(...begins) : 0;
        merged.eventEndTime = ends.length ? Math.max(...ends) : merged.eventBeginTime;
        return [merged];
    }

    private mapCardlessDrivingFromActivitiesByDay(eventsOnDays: OneDayActivity[]): Map<number, number> {
        const cardlessDrivingByDay = new Map<number, number>();
        (eventsOnDays || []).forEach(oneDayActivity => {
            cardlessDrivingByDay.set(oneDayActivity.recordDate,
                this.sumCardlessDrivingDurations(oneDayActivity.cardlessDrivingFromActivities));
        });
        return cardlessDrivingByDay;
    }

    private sumCardlessDrivingDurations(cardlessDrivingFromActivities: Fault[]): number {
        return (cardlessDrivingFromActivities || []).reduce((sum, item) => {
            if (item.duration !== undefined && item.duration !== null) {
                return sum + item.duration;
            }
            return sum + Math.max(0, Math.floor(((item.eventEndTime || 0) - (item.eventBeginTime || 0)) / 60));
        }, 0);
    }

    private isLastDownloadedDateToday(recordDate: number, lastDownloadedDate: number): boolean {
        const recordDateInMs = recordDate * 1000;
        return recordDateInMs < lastDownloadedDate && (recordDateInMs + this.getNumberOfHoursInDay(moment(recordDateInMs)) * 60 * 60 * 1000) > lastDownloadedDate;
    }

    private mapShiftsToOneWorkingTimeRows(shifts: Shift[], recordDate: number, timezone: string, language: string): OneWorkingTimeRow[] {
        const workingTimeRows = [];
        if (shifts) {
            shifts.forEach((shift) => {
                const workingTimeRow = new OneWorkingTimeRow();
                workingTimeRow.startDateHour = moment(shift.shiftFrom * 1000).myFormat('HH:mm');
                workingTimeRow.endDateHour = moment(shift.shiftTo * 1000).myFormat('HH:mm');
                workingTimeRow.startTime = (shift.shiftFrom - recordDate) / 60;
                const diffInSeconds = shift.shiftTo - recordDate;
                // if (diffInSeconds > 60 * 60 * 24) {
                //     diffInSeconds = diffInSeconds % (60 * 60 * 24);
                // }
                workingTimeRow.endTime = diffInSeconds / 60;
                workingTimeRow.startDayString = moment(shift.shiftFrom * 1000).myFormat('dddd');
                workingTimeRow.endDayString = moment(shift.shiftTo * 1000).myFormat('dddd');
                const workingTimeDetails = new WorkingTimeDetails();
                workingTimeDetails.driving = shift.driveInShift;
                workingTimeDetails.working = shift.workInShift;
                workingTimeDetails.availability = shift.availabilityInShift;
                workingTimeDetails.allWorksInShift = shift.allWorksInShift;
                workingTimeDetails.resting = shift.loggedBreakInShift;
                workingTimeDetails.shiftSumma = shift.shiftSumma;
                workingTimeDetails.restPeriod = shift.restPeriodForShift;
                workingTimeRow.workingTimeDetails = workingTimeDetails;
                workingTimeRows.push(workingTimeRow);
            });
        }
        return workingTimeRows;
    }

    private mapShiftsAndPlacesToInfolineItems(shifts: Shift[], places: Place[], recordDate: number): InfoLineitemDescription[] {
        const borderCrossingItems = [];
        if (shifts) {
            shifts.forEach(value => {
                if (value.shiftFrom - recordDate >= 0) {
                    const item = DailyChartService.mapPlaceToInfolineDescriptionItem(value.startPlace, recordDate, value.shiftFrom);
                    if (item) {
                        borderCrossingItems.push(item);
                    }
                }
                if ((value.shiftTo - (recordDate + 24 * 60 * 60)) <= 0) {
                    const item = DailyChartService.mapPlaceToInfolineDescriptionItem(value.endPlace, recordDate, value.shiftTo);
                    if (item) {
                        borderCrossingItems.push(item);
                    }
                }
            });
            places.map(value => {
                borderCrossingItems.push(DailyChartService.mapPlaceToInfolineDescriptionItem(value, recordDate, value.entryTime));
            })
        }
        return borderCrossingItems;
    }

    private mapShiftVehicleInfoToInfoLineItems(shifts: Shift[], recordDate: number): InfoLineitemDescription[] {
        const vehicleInfo = [];
        if (shifts) {
            shifts.forEach(shift => {
                shift.shiftVehicleInfo.forEach(shiftVehicleInfo => {
                    if (shift.shiftFrom - recordDate >= 0) {
                        const item = this.mapShiftVehicleInfoToInfolineDescriptionItem(shiftVehicleInfo, recordDate, shiftVehicleInfo.vehicleFirstUse);
                        if (item) {
                            vehicleInfo.push(item);
                        }
                    }
                })
            })
        }
        return vehicleInfo;
    }

    private mapShiftVehicleInfoToInfolineDescriptionItem(shiftVehicleInfo: ShiftVehicleInfo, recordDate: number, timestamp: number): InfoLineitemDescription {
        const infoLineitemDescription = new InfoLineitemDescription();
        if (shiftVehicleInfo !== undefined) {
            const it = new IconText();
            it.text = shiftVehicleInfo.registrationNumber + ' '
                + shiftVehicleInfo.registrationNation
                + (shiftVehicleInfo.registrationNation ? ' ' : '');
            it.icon = 'track_icon';
            infoLineitemDescription.iconTexts.push(it);
            const it2 = new IconText();
            it2.text = this.decimalPipe.transform(shiftVehicleInfo.odometerBegin, '', 'hu') + ' km '
                + this.decimalPipe.transform(shiftVehicleInfo.odometerEnd, '', 'hu') + ' km ';
            it2.icon = 'location_icon';
            infoLineitemDescription.iconTexts.push(it2);
            const it3 = new IconText();
            it3.text = this.decimalPipe.transform(shiftVehicleInfo.odometerSumma, '', 'hu') + ' km '
                + shiftVehicleInfo.avgSpeed + ' km/h';
            it3.icon = 'summa_icon';
            infoLineitemDescription.iconTexts.push(it3);
            infoLineitemDescription.crossingMinute = (timestamp - recordDate) / 60;
            infoLineitemDescription.extraInfo = shiftVehicleInfo.vehicleChange;
            return infoLineitemDescription;
        } else {
            return undefined;
        }
    }

    private mapFerryItemsToInfoLineItems(ferryItems: number[], recordDate: number): InfoLineitemDescription[] {
        const infoLineitemDescriptions = [];
        if (ferryItems) {
            ferryItems.forEach(ferryItem => {
                const infoLineItem = new InfoLineitemDescription();
                infoLineItem.crossingMinute = (ferryItem - recordDate) / 60;
                infoLineitemDescriptions.push(infoLineItem);
            })
        }
        return infoLineitemDescriptions;
    }

    private mapInfringmentItemsIntoDailyChartInput(infringments: Infringment[], timezone: string, language: string): Infringment[] {
        if (infringments) {
            infringments.forEach(item => {
                item.offenceStartMoment = moment(item.offenceStart * 1000);
                item.offenceEndMoment = moment(item.offenceEnd * 1000);
                item.offenceStartDayString = moment(item.offenceStart * 1000).locale(language).myFormat('dddd');
                item.offenceEndDayString = moment(item.offenceEnd * 1000).locale(language).myFormat('dddd');
            })
        }
        return infringments;
    }

    private mapOffenceItemsIntoDailyChartInput(offences: Offence[], timezone: string, language: string): Offence[] {
        if (!!offences) {
            offences.forEach(item => {
                item.offenceStartMoment = moment(item.startTime * 1000);
                item.offenceEndMoment = moment(item.endTime * 1000);
                item.offenceStartDayString = moment(item.startTime * 1000).locale(language).myFormat('dddd');
                item.offenceEndDayString = moment(item.endTime * 1000).locale(language).myFormat('dddd');
            });
        }
        return offences;
    }

    private mapDriversOnDayToInfoLineItems(driversOnDay: DriversOnDay[], recordDate: number, timezone: string): InfoLineitemDescription[] {
        const infoLineitemDescriptions = [];
        if (driversOnDay) {
            driversOnDay = this.filterDuplications(driversOnDay);
            driversOnDay = this.sortDriversOnDay(driversOnDay);
            driversOnDay.forEach(item => {
                let infoLineitemDescription = new InfoLineitemDescription();
                const it = new IconText();
                const isCoDriver = item.cardSlot === CardSlot.CODRIVER;
                infoLineitemDescription.crossingMinute = (item.useFrom - recordDate) / 60;
                it.icon = 'kormany';
                it.text = this.namePipe.transform('', item.firstName, item.surName,
                    this.translate.instant('Report.DailyChart.nameFormat'));
                infoLineitemDescription.iconTexts.push(it);
                infoLineitemDescription.extraInfo = isCoDriver;
                const currentLang = this.translate.currentLang;
                this.translate.currentLang = this.chartLanguage;
                const dateFormat = this.translate.instant('Report.General.TimeFormat');
                this.translate.currentLang = currentLang;
                const dateInterval = moment(item.startDateOfDriving * 1000).tz(timezone).myFormat(dateFormat)
                    + ' - ' + moment(item.endDateOfDriving * 1000).tz(timezone).myFormat(dateFormat);
                infoLineitemDescription.text = dateInterval;
                if (isCoDriver) {
                    const lastInfoLineItemDescription = infoLineitemDescriptions[infoLineitemDescriptions.length - 1];
                    if (!!lastInfoLineItemDescription && ((!lastInfoLineItemDescription.crossingMinute && !infoLineitemDescription.crossingMinute)
                        || DailyChartService.isTwoCrossingMinutesInOneMinute(lastInfoLineItemDescription?.crossingMinute, infoLineitemDescription.crossingMinute))) {
                        infoLineitemDescription = infoLineitemDescriptions[infoLineitemDescriptions.length - 1];
                        infoLineitemDescription.iconTexts.push(it);
                        infoLineitemDescription.extraInfo = isCoDriver;
                        infoLineitemDescriptions[infoLineitemDescriptions.length - 1] = infoLineitemDescription;
                        return;
                    }
                }
                infoLineitemDescriptions.push(infoLineitemDescription);
                const endDateOfDriving = item.endDateOfDriving - recordDate;
                if (endDateOfDriving > 0 && endDateOfDriving < 86400) {
                    const newInfoLine = new InfoLineitemDescription();
                    newInfoLine.crossingMinute = endDateOfDriving / 60;
                    newInfoLine.text = dateInterval;
                    const newIt = new IconText();
                    newIt.icon = 'down_arrow_icon';
                    newInfoLine.iconTexts.push(newIt);
                    infoLineitemDescriptions.push(newInfoLine);
                }
            })
        }
        return infoLineitemDescriptions;
    }

    private mapDrivingsWithoutCardToInfolineItems(drivingsWithoutCard: Fault[], recordDate: number): InfoLineitemDescription[] {
        return drivingsWithoutCard?.map(withoutCard => {
            const infoLineItem = new InfoLineitemDescription();
            infoLineItem.crossingMinute = (withoutCard.eventBeginTime - recordDate) / 60;
            const it = new IconText();
            it.icon = 'no_card_icon';
            it.text = Math.floor(
                withoutCard.duration !== undefined && withoutCard.duration !== null
                    ? Number(withoutCard.duration)
                    : (withoutCard.eventEndTime - withoutCard.eventBeginTime) / 60
            ).toString();
            infoLineItem.iconTexts.push(it);
            return infoLineItem;
        });
    }

    private mapFaults(faults: Fault[]): Fault[] {
        return faults?.map(fault => {
            DailyChartService.convertFaultCode(fault);
            return fault;
        }).sort(Fault.compare) || faults;
    }

    private filterDuplications(driversOnDay: DriversOnDay[]) {
        const filteredDriversOnDay: DriversOnDay[] = [];
        driversOnDay.forEach(value => {
            if (filteredDriversOnDay.filter(item => {
                return item.firstName === value.firstName && item.surName === value.surName
                    && item.useFrom === value.useFrom && item.cardSlot === value.cardSlot
            }).length === 0) {
                filteredDriversOnDay.push(value);
            }
        })
        return filteredDriversOnDay;
    }

    private sortDriversOnDay(driversOnDay: DriversOnDay[]): DriversOnDay[] {
        return driversOnDay.sort((a, b) => {
            if (!a.useFrom && b.useFrom) {
                return -1;
            }

            if (!b.useFrom && a.useFrom) {
                return 1;
            }

            if ((!a.useFrom && !b.useFrom) || DailyChartService.isTwoEpocInOneMinute(a.useFrom, b.useFrom)) {
                if (a.cardSlot === CardSlot.DRIVER && b.cardSlot === CardSlot.CODRIVER) {
                    return -1;
                }

                if (a.cardSlot === CardSlot.CODRIVER && b.cardSlot === CardSlot.DRIVER) {
                    return 1;
                }

                if (a.cardSlot === b.cardSlot) {
                    return 0;
                }
            }

            if (a.useFrom < b.useFrom) {
                return -1;
            }

            if (b.useFrom < a.useFrom) {
                return 1;
            }

            // if (a.cardSlot === CardSlot.DRIVER && b.cardSlot === CardSlot.CODRIVER) {
            //     return -1;
            // }
            //
            // if (a.cardSlot === CardSlot.CODRIVER && b.cardSlot === CardSlot.DRIVER) {
            //     return 1;
            // }

            // if (a.cardSlot === b.cardSlot) {
            //     return 0;
            // }
        });
    }

    onMenuChange(): EventEmitter<any> {
        return this.changeMenuEmitter;
    }

    menuChanged() {
        this.changeMenuEmitter.emit();
    }

    dataReadyToPrint(tab: TabOption) {
        const format = 'YYYY-MM-DD';
        const reportContext = this.getReportContext(tab).reportContext;
        const startDate = moment(reportContext.periodStartTime * 1000).myFormat(format);
        const endDate = moment(reportContext.periodEndTime * 1000).myFormat(format);
        const timezone = reportContext.dailyChartResponses[0]?.timeZone || 'UTC';
        let entityOrCompany = this.companyName?.replaceAll(' ', '_') || 'Company_wide';
        if (reportContext.dailyChartResponses.length === 1) {
            const entityData = reportContext.dailyChartResponses[0];
            if (tab === TabOption.DRIVER) {
                entityOrCompany = this.nameFormatService.formatName(
                    entityData.driverInfo.cardHolderFirstNames,
                    entityData.driverInfo.cardHolderSurname
                ).toUpperCase().replaceAll(' ', '_');
            } else {
                entityOrCompany = entityData.vehicleInfo.registrationNumber.replaceAll(' ', '_');
            }
        }

        const title = document.title;
        document.title = [startDate, endDate, 'Daily', 'chart', tab.toLowerCase(), entityOrCompany, 'diagram', timezone].join('_');
        setTimeout(() => {
            window.print();
            document.title = title;
        });
    }

    getCurrentCompanyName(currentCompanyId: number) {
        this.companyService.getAllSubcompanies()
            .pipe(take(1))
            .subscribe((response) => {
                const availableCompanies = response;

                availableCompanies.sort(function (a, b) {
                    const x = a.name.toLowerCase();
                    const y = b.name.toLowerCase();
                    if (x < y) {
                        return -1;
                    }
                    if (x > y) {
                        return 1;
                    }
                    return 0;
                });

                const selectedLocalCompany: Company = availableCompanies.find((aCopmany) => aCopmany.id === currentCompanyId);
                this.printService.companyName = selectedLocalCompany.name;
            });
    }

    getDailyChartTabEnumByValue(value: string): TabOption {
        switch (value) {
            case 'DRIVER':
                return TabOption.DRIVER;
            case 'VEHICLE':
                return TabOption.VEHICLE;
        }
    }

    dateChange(event: MatDatepickerInputEvent<Date>) {
        // idiot hack because of datePicker weakness
        if (event.target instanceof MatStartDate) {
            this.reportContexts.forEach(value => {
                value.reportContext.chartQueryForm.get('rangeFormGroup').get('start').setValue(event.target.value);
            })
        }
        if (event.target instanceof MatEndDate) {
            this.reportContexts.forEach(value => {
                value.reportContext.chartQueryForm.get('rangeFormGroup').get('end').setValue(event.target.value);
            })
        }
        if (event.target instanceof MatStartDate || event.target instanceof MatEndDate) {
            this.reportContexts.forEach(value => {
                value.reportContext.chartQueryForm.get('preselector').setValue(DateIntervalOptions.CUSTOM);
            });
        }
    }

    private startDailyChartProgressBar() {
        this.progressBarService.needed.next(true);
        this.progressBarService.mode.next('query');
    }

    private stopDailyChartProgressBar() {
        this.progressBarService.needed.next(false);
        this.printService.progressBarNeeded = false;
    }

    public abortQuery() {
        this.dailyChartSubscription?.unsubscribe();
    }

    private resetReportContext(tab: TabOption) {
        this.getReportContext(tab).reportContext.chartDataReady = false;
        this.getReportContext(tab).reportContext.dailyChartResponses = [];
        this.getReportContext(tab).reportContext.dailyChartInputsTemp = [];
        this.getReportContext(tab).reportContext.dailyChartInputsPaged = [];
        this.getReportContext(tab).reportContext.dailyChartInputsPagedTemp = [];
        this.getReportContext(tab).reportContext.dailyChartInputs = [];
        this.getReportContext(tab).reportContext.indexOfLastChartToLoad = -1;
        this.getReportContext(tab).reportContext.selectedDriverIndex = 0;
    }

    public getNumberOfHoursInDay(m: Moment): number {
        const a = moment(m).startOf('day');
        const b = moment(m).add(1, 'day').startOf('day');
        return b.diff(a, 'hours');
    }
}
